import React, { useMemo, useRef } from 'react';
import { TableCell, TableRow } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import type { TAutocompleteOption } from '~/components/AutocompleteField/types.ts';
import { FormAutocomplete } from '~/components/FormComponents/FormAutocomplete.tsx';
import { FormInput } from '~/components/FormComponents/FormInput.tsx';
import { useNewKvedQuery } from '~/queries/useNewKvedQuery.ts';
import { FinancialConditionTableCell } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/components/shared/FinancialConditionTableCell.tsx';
import { FinancialConditionTableDeleteCell } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/components/shared/FinancialConditionTableDeleteCell.tsx';
import { normalizeDecimalValue } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/utils.ts';
import type { TIncomeStructureByKvedFormValues } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/incomeStructureByKved/types.ts';
import { useKvedByCodeMutation } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/incomeStructureByKved/useKvedByCodeMutation.ts';
import { documentGenerationFormStyles } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/styles.ts';

interface IIncomeStructureByKvedIncomeRowProps {
  index: number;
  canRemove: boolean;
  disabled: boolean;
  onRemove: () => void;
}

export const IncomeStructureByKvedIncomeRow: React.FC<IIncomeStructureByKvedIncomeRowProps> = ({
  index,
  canRemove,
  disabled,
  onRemove,
}) => {
  const { clearErrors, setError, setValue } = useFormContext<TIncomeStructureByKvedFormValues>();
  const lookupRequestIdRef = useRef(0);
  const { mutateAsync: findKvedByCode } = useKvedByCodeMutation();
  const { rowKveds } = useNewKvedQuery();
  const kvedOptions = useMemo<TAutocompleteOption[]>(
    () =>
      (rowKveds ?? [])
        .map(({ code }) => ({ label: code, value: code }))
        .sort((firstOption, secondOption) => firstOption.label.localeCompare(secondOption.label)),
    [rowKveds]
  );
  const kvedCodeFieldName = `incomes.${index}.kvedCode` as const;
  const economicActivityGroupFieldName = `incomes.${index}.economicActivityGroup` as const;

  const handleKvedChange = async (
    value: TAutocompleteOption | TAutocompleteOption[] | null
  ) => {
    if (Array.isArray(value)) {
      return;
    }

    const code = value?.label.trim() ?? '';
    const requestId = ++lookupRequestIdRef.current;

    if (!code) {
      setValue(kvedCodeFieldName, null, { shouldDirty: true, shouldTouch: true });
      setValue(economicActivityGroupFieldName, '', { shouldDirty: true, shouldTouch: true });
      clearErrors(kvedCodeFieldName);
      return;
    }

    try {
      const kved = await findKvedByCode(code);

      if (requestId !== lookupRequestIdRef.current) {
        return;
      }

      setValue(
        kvedCodeFieldName,
        { label: kved.code, value: kved.code },
        { shouldDirty: true, shouldTouch: true }
      );
      setValue(economicActivityGroupFieldName, kved.name, {
        shouldDirty: true,
        shouldTouch: true,
      });
      clearErrors(kvedCodeFieldName);
    } catch {
      if (requestId !== lookupRequestIdRef.current) {
        return;
      }

      setValue(kvedCodeFieldName, null, { shouldDirty: true, shouldTouch: true });
      setValue(economicActivityGroupFieldName, '', { shouldDirty: true, shouldTouch: true });
      setError(kvedCodeFieldName, { type: 'validate', message: 'КВЕД не знайдено' });
    }
  };

  return (
    <TableRow>
      <TableCell align="center">{index + 1}</TableCell>
      <FinancialConditionTableCell>
        <FormAutocomplete
          name={kvedCodeFieldName}
          options={kvedOptions}
          label="Код за КВЕД 2010"
          disabled={disabled}
          fullWidth
          shouldSaveLabel
          inputProps={{ inputMode: 'numeric' }}
          onChange={handleKvedChange}
          sx={documentGenerationFormStyles.field}
        />
      </FinancialConditionTableCell>
      <FinancialConditionTableCell>
        <FormInput
          name={economicActivityGroupFieldName}
          label="Групи видів економічної діяльності"
          disabled={disabled}
          fullWidth
          sx={documentGenerationFormStyles.field}
        />
      </FinancialConditionTableCell>
      <FinancialConditionTableCell>
        <FormInput
          name={`incomes.${index}.incomeAmount`}
          label="Сума доходу, тис. грн."
          disabled={disabled}
          fullWidth
          type="text"
          inputProps={{ inputMode: 'decimal' }}
          normalizeOnBlur={normalizeDecimalValue}
          sx={documentGenerationFormStyles.field}
        />
      </FinancialConditionTableCell>
      <FinancialConditionTableDeleteCell
        label={`Видалити дохід ${index + 1}`}
        disabled={disabled}
        hidden={!canRemove}
        onDelete={onRemove}
      />
    </TableRow>
  );
};
